import DOMPurify from 'dompurify';

/**
 * HTML ajeno que va a vivir DENTRO de la aplicación (el editor del redactor
 * es un contenteditable del documento principal, no un iframe con sandbox
 * como el lector). Sin sanear, un correo con `<img src=x onerror=…>` ejecutaba
 * código con la sesión del usuario al pulsar Responder o Reenviar (SEG-1).
 *
 * Instancia propia de DOMPurify: los ganchos de abajo no deben alterar al
 * lector, que usa la instancia por defecto.
 */
const purificador = DOMPurify(window);

purificador.addHook('afterSanitizeAttributes', (nodo) => {
  // Las clases del correo no pintan nada aquí (su <style> se quita) y podían
  // coincidir con clases de la app (`fixed inset-0 z-50`) y tapar la pantalla.
  // Solo se conservan las propias del redactor (`zuno-signature`).
  if (nodo.hasAttribute && nodo.hasAttribute('class')) {
    const propias = nodo.getAttribute('class').split(/\s+/).filter((c) => c.startsWith('zuno-'));
    if (propias.length) nodo.setAttribute('class', propias.join(' '));
    else nodo.removeAttribute('class');
  }
  if (nodo.removeAttribute) nodo.removeAttribute('id');
  // Un estilo en línea con position fixed/absolute también se sale del editor.
  const pos = nodo.style && nodo.style.position;
  if (pos && pos !== 'static' && pos !== 'relative') nodo.style.removeProperty('position');
});

const ETIQUETAS_FUERA = [
  'style', 'link', 'meta', 'base', 'title',                       // tocarían el CSS o las URLs de toda la app
  'form', 'input', 'button', 'textarea', 'select', 'option',      // controles vivos dentro del editor
];

/** HTML listo para meter en el editor del redactor.
 *  `bloquearImagenes`: las imágenes remotas no se cargan (R9, auditoría 2026-10-05). Al responder o
 *  reenviar un correo cuyas imágenes el lector tenía bloqueadas (Spam), la cita las cargaba y el píxel
 *  espía confirmaba al remitente que la dirección existe. */
export function htmlParaEditor(html, { bloquearImagenes = false } = {}) {
  const limpio = purificador.sanitize(String(html ?? ''), { FORBID_TAGS: ETIQUETAS_FUERA, ADD_ATTR: ['target'] });
  if (!bloquearImagenes || typeof document === 'undefined') return limpio;
  const t = document.createElement('template');
  t.innerHTML = limpio;
  t.content.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') || '';
    if (/^(https?:)?\/\//i.test(src) || /\/api\//.test(src)) {
      img.removeAttribute('src');
      img.setAttribute('alt', img.getAttribute('alt') || '[imagen bloqueada]');
    }
  });
  t.content.querySelectorAll('[style]').forEach((el) => {
    const st = el.getAttribute('style') || '';
    if (/url\s*\(/i.test(st)) el.setAttribute('style', st.replace(/url\s*\([^)]*\)/gi, 'none'));
  });
  return t.innerHTML;
}

/** Texto plano escapado para interpolarlo dentro de HTML. */
export function escaparHtml(texto) {
  return String(texto ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
